import { AlertCircle, AlertTriangle } from "lucide-react";
import { useMemo, useState } from "react";

import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import { useFeeLabels } from "../../hooks/useFeeLabels";
import { useFeeMutations } from "../../hooks/useFeeMutations";
import type { PaymentFee, ProviderRailRoute } from "../../types/paymentFees.types";
import { corridorLabel, FEE_STATUS_PILL_VARIANT, formatFeeFormula } from "../../utils/feeFormat";
import { feeStatusOf } from "../../utils/feeMath";
import { specialPriceRemovalPlan } from "../../utils/feeOverrides";
import { CorridorIdentity } from "./CorridorIdentity";

export interface RemoveSpecialPriceDialogProps {
  route: ProviderRailRoute;
  organizationId: string;
  organizationName: string;
  open: boolean;
  onClose: () => void;
  /** Called once every version is gone; a partial run keeps the dialog open. */
  onRemoved: () => void;
  routes: ProviderRailRoute[];
  fees: PaymentFee[];
  now: number;
}

export function RemoveSpecialPriceDialog({
  route,
  organizationId,
  organizationName,
  open,
  onClose,
  onRemoved,
  routes,
  fees,
  now,
}: RemoveSpecialPriceDialogProps) {
  const { t, activeLanguage } = useTranslations("paymentFees");
  const labels = useFeeLabels();
  const { isSubmitting, error, removeInOrder } = useFeeMutations();
  const [removed, setRemoved] = useState(0);
  const [partial, setPartial] = useState(false);

  const plan = useMemo(
    () => specialPriceRemovalPlan(route, organizationId, fees, routes, now),
    [route, organizationId, fees, routes, now],
  );
  const rows = plan?.rows ?? [];
  const pending = rows.slice(removed);
  const corridorText = corridorLabel(route, labels.directionShort);

  const handleConfirm = async () => {
    const outcome = await removeInOrder(
      pending.map((fee) => fee.id),
      (count) => setRemoved(removed + count),
    );
    if (outcome.completed) onRemoved();
    else setPartial(true);
  };

  const formula = (fee: PaymentFee) => formatFeeFormula(fee, activeLanguage, labels.formula);

  return (
    <ColocaAlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isSubmitting) onClose();
      }}
      title={t("remove_title")}
      description={t("remove_description", { name: organizationName, corridor: corridorText })}
      confirmLabel={
        isSubmitting ? t("remove_progress", { done: removed, total: rows.length }) : t("remove_confirm")
      }
      confirmVariant="destructive"
      confirmDisabled={isSubmitting || !plan}
      cancelLabel={t("delete_cancel")}
      onConfirm={() => void handleConfirm()}
      onCancel={onClose}
    >
      {plan && (
        <div className="space-y-4">
          {plan.impact.length > 1 && (
            <div className="space-y-1.5 text-sm">
              <p>{t("remove_broad_note", { count: plan.impact.length })}</p>
              <div className="flex flex-col gap-1.5">
                {plan.impact.map((impact) => (
                  <CorridorIdentity key={impact.route.id} route={impact.route} />
                ))}
              </div>
            </div>
          )}

          <section className="space-y-1.5">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("remove_after_title")}
            </h3>
            <ul className="space-y-1.5">
              {plan.impact.map((impact) => (
                <li key={impact.route.id} className="text-sm">
                  <CorridorIdentity route={impact.route} className="mb-1" />
                  {impact.after == null ? (
                    <ColocaAlert
                      variant="destructive"
                      icon={AlertTriangle}
                      text={t("remove_after_none", { name: organizationName })}
                    />
                  ) : (
                    <span className="font-mono">
                      {impact.after.organization_id
                        ? t("remove_after_other_special", { price: formula(impact.after) })
                        : t("remove_after_general", { price: formula(impact.after) })}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>

          <section className="space-y-1.5">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("remove_versions_title")}
            </h3>
            <ul className="divide-y divide-border rounded-xl border border-border">
              {rows.map((fee, index) => {
                const status = feeStatusOf(fee, rows, now);
                return (
                  <li
                    key={fee.id}
                    className={
                      index < removed
                        ? "flex items-center gap-2 px-3 py-2 opacity-50 line-through"
                        : "flex items-center gap-2 px-3 py-2"
                    }
                  >
                    <ColocaBadge variant="default" className="text-muted-foreground">
                      v{fee.version}
                    </ColocaBadge>
                    <span className="min-w-0 flex-1 truncate font-mono text-xs">{formula(fee)}</span>
                    <span className="hidden text-[11px] text-muted-foreground sm:inline">
                      {formatDateTime(fee.valid_from, { locale: activeLanguage })}
                    </span>
                    <ColocaStatusPill variant={FEE_STATUS_PILL_VARIANT[status]} size="sm">
                      {labels.status[status]}
                    </ColocaStatusPill>
                  </li>
                );
              })}
            </ul>
          </section>

          {partial && !isSubmitting && (
            <ColocaAlert
              variant="warning"
              icon={AlertTriangle}
              text={t("remove_partial", { done: removed, total: rows.length })}
            />
          )}
          {error && <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />}
        </div>
      )}
    </ColocaAlertDialog>
  );
}
